<template>
  <div class="pa-6">
    <!-- Breadcrumb and back button -->
    <v-row class="align-center bread-row">
      <v-col cols="12" md="10">
        <Breadcrump :crumbs="crumbs" />
      </v-col>
      <v-col cols="12" md="2" class="d-flex justify-end">
        <v-btn @click="$router.go(-1)" fab small dark color="#1976d2">
          <v-icon dark>mdi-arrow-left</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Date Range Filter -->
    <v-row class="align-center calendar-row">
      <v-col cols="12" md="3">
        <div class="calendar-wrapper">
          <p class="calendar-label">از:</p>
          <date-picker
            simple
            editable
            required
            v-model="from"
            placeholder="انتخاب تاریخ"
          />
        </div>
      </v-col>
      <v-col cols="12" md="3">
        <div class="calendar-wrapper">
          <p class="calendar-label">تا:</p>
          <date-picker
            simple
            editable
            required
            v-model="to"
            placeholder="انتخاب تاریخ"
          />
        </div>
      </v-col>
      <v-col cols="12" md="3" class="d-flex align-center">
        <v-btn
          :loading="loading"
          color="#1976d2"
          class="mt-md-6 mt-2 white--text"
          @click="loadData"
        >
          مشاهده
        </v-btn>
      </v-col>
    </v-row>

    <v-card class="pa-4 elevation-3">
      <v-card-title class="title-font">گزارش نسخه های دستگاه</v-card-title>
      <v-data-table
        :loading="loading"
        :headers="otaHeaders"
        :items="filteredOtaLogs"
        dense
        class="elevation-1"
        :items-per-page="10"
        :footer-props="{
          showFirstLastPage: true,
          firstIcon: 'mdi-arrow-collapse-left',
          lastIcon: 'mdi-arrow-collapse-right',
          prevIcon: 'mdi-arrow-left',
          nextIcon: 'mdi-arrow-right',
          itemsPerPageOptions: [5, 10, 25, 50]
        }"
        no-data-text="داده‌ای برای نمایش در این بازه وجود ندارد"
      >
        <template v-slot:item.createdAt="{ item }">
          {{ formatJalaliDate(item.createdAt) }}
        </template>
        <template v-slot:item.state="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                :color="getStatusChipColor(item.state, 'state')"
                v-bind="attrs"
                v-on="on"
              >
                {{ formatStatus(item.state, "state") }}
              </v-icon>
            </template>
            <span>{{ formatDescStatus(item.state, "state") }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import moment from "moment-jalaali";
import request from "@/utils/request";
import Breadcrump from "@/components/common/Breadcrump.vue";

export default {
  name: "DeviceOtaLogs",
  components: { Breadcrump },
  data() {
    const today = moment().format("jYYYY/jMM/jDD");
    const oneMonthAgo = moment()
      .subtract(1, "months")
      .format("jYYYY/jMM/jDD");
    return {
      crumbs: [
        { text: "خانه", disabled: false, href: "/" },
        { text: "مجموعه ها", disabled: false, href: "/complexes" },
        {
          text: `مجموعه ${this.$store.state.selectedComplexName || ""}`,
          disabled: false,
          href: `/complex-units/${this.$store.state.selectedComplexId}`
        },
        {
          text: this.$store.state.selectedDeviceName || "دستگاه",
          href: `/complex-devices/${this.$store.state.selectedComplexId}`,
          disabled: false
        },
        { text: "گزارش نسخه های دستگاه", disabled: true }
      ],
      from: oneMonthAgo,
      to: today,
      deviceId: this.$route.params.deviceId,
      otaLogs: [],
      filteredOtaLogs: [],
      loading: false,
      statusFieldMap: {
        state: {
          1: "mdi-check-circle",
          2: "mdi-alert-circle",
          3: "mdi-alert-circle",
          4: "mdi-update",
          5: "mdi-restore",
          6: "mdi-not-equal"
        }
      },
      statusFieldDescMap: {
        state: {
          1: "آپدیت موفق",
          2: "آپدیت ناموفق",
          3: "بازیابی نسخه پیشین ناموفق",
          4: "آپدیت دستی",
          5: "نسخه پیشین بازیابی شد",
          6: "نسخه فعلی با نسخه آپدیت ناهمخوانی دارد"
        }
      }
    };
  },
  computed: {
    otaHeaders() {
      return [
        { text: "نسخه", value: "version" },
        { text: "وضعیت", value: "state" },
        { text: "پیام", value: "message" },
        { text: "زمان", value: "createdAt" }
      ];
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const gregorianFrom = moment(this.from, "jYYYY/jMM/jDD").format(
          "YYYY-MM-DD"
        );
        const gregorianTo = moment(this.to, "jYYYY/jMM/jDD").format(
          "YYYY-MM-DD"
        );
        const response = await request.get(
          `/deviceotalist/${this.deviceId}/logs`,
          {
            params: { from: gregorianFrom, to: gregorianTo },
            headers: { accept: "application/json" }
          }
        );

        this.otaLogs = response.data.result || [];
        this.filteredOtaLogs = this.otaLogs;
        this.filteredOtaLogs.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
      } catch (error) {
        this.otaLogs = [];
        this.filteredOtaLogs = [];
      } finally {
        this.loading = false;
      }
    },
    formatStatus(value, key) {
      return this.statusFieldMap[key]?.[value] ?? value;
    },
    formatDescStatus(value, key) {
      return this.statusFieldDescMap[key]?.[value] ?? value;
    },
    getStatusChipColor(value, key) {
      const colors = {
        state: {
          1: "green darken-1",
          2: "red darken-1",
          3: "red darken-1",
          4: "grey darken-1",
          5: "yellow darken-1",
          6: "yellow darken-1"
        }
      };
      return colors[key]?.[value] || "blue darken-1";
    },
    formatJalaliDate(datetime) {
      if (!datetime) return "-";
      try {
        return moment(datetime).format("jYYYY/jMM/jDD HH:mm:ss");
      } catch {
        return "-";
      }
    }
  }
};
</script>

<style scoped>
.IranSans {
  font-family: "IranSans", sans-serif !important;
}

.pa-6 {
  padding: 4px !important;
  min-height: 100vh;
}

.bread-row {
  margin-bottom: 4px !important;
  margin-top: -8px !important;
}

.calendar-row {
  margin-top: 8px !important;
  margin-bottom: 24px;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.calendar-wrapper {
  display: flex;
  flex-direction: column;
}

.calendar-label {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #333;
}

.v-card {
  border-radius: 16px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06) !important;
  transition: all 0.2s ease;
}

.v-card:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
}

.v-card-title {
  font-weight: 600;
  font-size: 17px !important;
  color: #1a1a1a;
}

.title-font {
  font-family: "IranSans", sans-serif !important;
  font-weight: 600 !important;
}

.v-data-table {
  font-size: 13.5px;
  border-radius: 12px;
  overflow: hidden;
}

.v-chip {
  font-size: 12px;
  font-weight: 500;
}

@media (max-width: 960px) {
  .calendar-row {
    gap: 12px;
  }
}

@media (max-width: 600px) {
  .pa-6 {
    padding: 12px !important;
  }
  .v-card-title {
    font-size: 16px !important;
  }
}
</style>
